import { useMemo } from 'react';
import { tiptapDocToPlainText } from '@odo/shared-ui';
import { usePatientNotes } from '../../../hooks/usePatientNotes';
import type { PatientNote } from '../../../services/patient/notes';
import { formatCreatedDate } from '../../../utils/dates';
import { PinIcon } from '../notes/PinIcon';

const NOTES_PREVIEW_COUNT = 2;

interface LatestNotesCardProps {
    patientId: string | null;
}

function notePreviewText(note: PatientNote): string {
    if (note.bodyDoc) {
        const plain = tiptapDocToPlainText(note.bodyDoc);
        if (plain) return plain;
    }
    return note.body;
}

/** Latest 2 notes (pinned first, same grouping as the Notes tab), reusing the
 *  page's shared `usePatientNotes` cache — no extra network request. Each
 *  preview row uses the same pinned/unpinned tone classes as the Notes tab's
 *  `NoteCard` (yellow for pinned, blue for regular) so a note reads
 *  consistently wherever it appears on this page. */
function LatestNotesCard({ patientId }: LatestNotesCardProps) {
    const { notes, isLoading } = usePatientNotes(patientId ?? undefined, {
        enabled: !!patientId
    });

    const previewNotes = useMemo(() => {
        const pinned = notes.filter((note) => note.pinned);
        const unpinned = notes.filter((note) => !note.pinned);
        return [...pinned, ...unpinned].slice(0, NOTES_PREVIEW_COUNT);
    }, [notes]);

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 h-full min-h-0 flex flex-col overflow-hidden gap-3">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 shrink-0">
                Latest notes
            </span>

            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-subtle">
                {isLoading ? (
                    <div className="flex flex-col gap-2">
                        <div className="h-12 bg-gray-100 rounded-xl animate-pulse" />
                        <div className="h-12 bg-gray-100 rounded-xl animate-pulse" />
                    </div>
                ) : previewNotes.length === 0 ? (
                    <div className="h-full flex items-center justify-center">
                        <span className="text-sm text-gray-500">No notes yet</span>
                    </div>
                ) : (
                    <div className="flex flex-col gap-2">
                        {previewNotes.map((note) => (
                            <div
                                key={note.id}
                                className={`rounded-xl border px-3 py-2.5 flex flex-col gap-0.5 min-w-0 ${
                                    note.pinned
                                        ? 'border-yellow-200 bg-yellow-50'
                                        : 'border-blue-200 bg-blue-50'
                                }`}
                            >
                                <div className="flex items-center gap-1.5 min-w-0">
                                    {note.pinned && (
                                        <PinIcon className="w-3 h-3 text-yellow-700 shrink-0" />
                                    )}
                                    <span className="text-sm font-semibold text-gray-800 truncate">
                                        {note.title}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-600 line-clamp-2">
                                    {notePreviewText(note)}
                                </p>
                                <span className="text-[11px] text-gray-500">
                                    {formatCreatedDate(note.createdAt)}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export { LatestNotesCard };
